import type {ClientFetch} from '../util/clientFetch';
import type {
  ConversationsApiDataConversationOverview,
  ConversationsApiDataConversationStatus,
  ConversationsApiListConversationsEndpoint,
  ConversationsApiListConversationsEndpointResponse,
} from '../../../generated/types';

export type ConversationOverview = ConversationsApiDataConversationOverview;
export type ConversationsApiConversationStatus =
  ConversationsApiDataConversationStatus;

export type ListConversationsRequest = Omit<
  ConversationsApiListConversationsEndpoint,
  'entry_point' | 'trigger_point' | 'status' | 'limit' | 'conversation_ids'
> & {
  entryPoint: ConversationsApiListConversationsEndpoint['entry_point'];
  triggerPoint?: ConversationsApiListConversationsEndpoint['trigger_point'];
  status?: ConversationsApiListConversationsEndpoint['status'];
  limit?: ConversationsApiListConversationsEndpoint['limit'];
  conversationIds?: ConversationsApiListConversationsEndpoint['conversation_ids'];
};

type ListConversationsErrorResponse = Extract<
  ConversationsApiListConversationsEndpointResponse,
  {type: 'error'}
> & {
  code?: number;
};

export type ListConversationsResponse =
  | Extract<
      ConversationsApiListConversationsEndpointResponse,
      {type: 'success'}
    >
  | ListConversationsErrorResponse;

type RawListConversationsResponse =
  ConversationsApiListConversationsEndpointResponse;

function buildListConversationsPath({
  audience,
  entryPoint,
  triggerPoint,
  status,
  limit,
  conversationIds,
}: ListConversationsRequest): string {
  const query = new URLSearchParams({
    audience,
    entry_point: entryPoint,
  });

  if (triggerPoint) {
    query.set('trigger_point', triggerPoint);
  }

  if (status) {
    query.set('status', status);
  }

  if (typeof limit === 'number') {
    query.set('limit', limit.toString());
  }

  conversationIds?.forEach((id) => {
    query.append('conversation_ids[]', id);
  });

  return `/api/v2/conversations?${query.toString()}`;
}

export async function listConversations(
  supportFetch: ClientFetch,
  request: ListConversationsRequest,
): Promise<ListConversationsResponse> {
  const response = await supportFetch(buildListConversationsPath(request));

  let data: RawListConversationsResponse | null = null;
  try {
    data = (await response.json()) as RawListConversationsResponse;
  } catch {
    return {
      type: 'error',
      error: 'Failed to parse list conversations response',
      code: response.status,
    };
  }

  if (data == null) {
    return {
      type: 'error',
      error: 'Failed to parse list conversations response',
      code: response.status,
    };
  }

  if (!response.ok) {
    return {
      type: 'error',
      error:
        data.type === 'error' ? data.error : 'Failed to list conversations',
      code: response.status,
    };
  }

  if (data.type === 'error') {
    return {
      ...data,
      code: response.status,
    };
  }

  return data;
}
